import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Button from "../../components/form/Button";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { handleFilterChange } from "../../helpers";
import DeleteModal from "../../components/common/DeleteModal";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import useMutation from "../../hooks/useMutation";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import {
  categoryDelete,
  categoryList,
  categoryOrdering,
  categoryStatusChange,
} from "../../services/catagoryService";
import ManageCategoryModal from "../../components/sections/admin/category/modals/ManageCategoryModal";
import { useParams } from "react-router-dom";

const Category = () => {
  setTitle("Plotics Admin | Application Form");
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  // const { categoryType } = useParams();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 50,
    page: 1,
    // sortQuery: "ordering",
  });

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["applicationForm", filters, search],
    fetchFunction: async () => {
      const {
        isParent,
        status = [],
        isFeatured = [],
        ...restFilters
      } = filters || {};
      const res = await categoryList({
        ...restFilters,
        // type: categoryType,
        ...(search && { keyword: search }),
        ...(typeof isParent === "boolean" && { isParent }),
        ...Object.fromEntries(status.map((val, i) => [`status[${i}]`, val])),
        ...Object.fromEntries(
          isFeatured.map((val, i) => [`isFeatured[${i}]`, val])
        ),
      });
      return res?.docs;
    },
  });

  //   Application Form status change handler =================>
  // const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
  //   useMutation({
  //     mutationFn: async ({ id, status }) => {
  //       const res = await categoryStatusChange({
  //         ids: id,
  //         status,
  //       });
  //       return { res, payload: { id, status } };
  //     },
  //     onSuccess: ({ res, payload }) => {
  //       if (res?.status !== 200) {
  //         updateData((pre) => {
  //           pre.data = (pre?.data || []).map((elm) =>
  //             elm?._id === payload?.id
  //               ? { ...elm, status: payload?.status === 1 ? 2 : 1 }
  //               : elm
  //           );
  //           return { ...pre };
  //         });
  //       }
  //     },
  //     onError: (error) => {
  //       console.log(error);
  //       toast.error(error?.response?.data?.message || "Status change failed!");
  //     },
  //   });
  //   category status and featured change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status, featured }) => {
        const payload = {
          ids: id,
          ...(typeof status !== "undefined" && { status }),
          ...(typeof featured !== "undefined" && { featured }),
        };

        const res = await categoryStatusChange(payload);
        return { res, payload };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) => {
              if (elm?._id === payload?.ids) {
                return {
                  ...elm,
                  ...(payload.status && {
                    status: payload.status === 1 ? 2 : 1,
                  }),
                  ...(payload.featured && {
                    featured: payload.featured === 1 ? 2 : 1,
                  }),
                };
              }
              return elm;
            });
            return { ...pre };
          });
        }
      },
      onError: (error) => {
        console.error("Status change error:", error);
      },
    });

  const tableHeadData = [
    {
      _id: 1,
      name: "Category Name",
      align: "left",
    },
    {
      _id: 2,
      name: "Category Type",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        const selected = val?.map((item) => item?.value) || [];

        setFilters((pre) => {
          const updated = { ...pre, page: 1 };

          if (selected.length === 1) {
            updated.isParent = selected[0] === 1; // 1 = Parent, 2 = Sub Category
          } else {
            // Remove isParent if both or none are selected
            delete updated.isParent;
          }

          return updated;
        });
      },

      filterData: [
        { name: "Parent", value: 1, type: "checkbox" },
        { name: "Sub", value: 2, type: "checkbox" },
      ],
    },
    {
      _id: "featured",
      name: "Featured",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            isFeatured: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.isFeatured;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Featured", value: 1, type: "checkbox" },
        { name: "Unfeatured", value: 2, type: "checkbox" },
      ],
    },
    {
      _id: "status",
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Active", value: 1, type: "checkbox" },
        { name: "Inactive", value: 2, type: "checkbox" },
      ],
    },
    {
      _id: 4,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: `${item?.name || ""}`.trim() || "-",
          // data:
          //   (
          //     <div
          //       dangerouslySetInnerHTML={{
          //         __html: item?.name,
          //       }}
          //     ></div>
          //   ) || "-",
          image: item?.image?.url,
        },
        // {
        //   _id: 2,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data:
        //     (
        //       <div
        //         dangerouslySetInnerHTML={{
        //           __html:
        //             item?.type?.charAt(0).toUpperCase() +
        //               item?.type?.slice(1) || "-",
        //         }}
        //       ></div>
        //     ) || "-",
        // },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <Badge
              type={item?.isParent === true ? "success" : "info"}
              label={item?.isParent === true ? "Parent" : "Sub"}
              className="!w-20"
            />
          ),
        },
        {
          _id: "featured",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.featured === 1}
                onChange={() => {
                  updateData((pre) => {
                    pre.data = (pre?.data || []).map((elm) =>
                      elm?._id === item?._id
                        ? { ...elm, featured: elm?.featured === 1 ? 2 : 1 }
                        : elm
                    );
                    return { ...pre };
                  });
                  statusChangeHandler({
                    id: item?._id,
                    featured: item?.featured === 1 ? 2 : 1,
                  });
                }}
              />
              <Badge
                type={item?.featured === 1 ? "success" : "danger"}
                label={item?.featured === 1 ? "Featured" : "UnFeatured"}
                className="!w-20"
              />
            </div>
          ),
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.status === 1}
                onChange={() => {
                  updateData((pre) => {
                    pre.data = (pre?.data || []).map((elm) =>
                      elm?._id === item?._id
                        ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
                        : elm
                    );
                    return { ...pre };
                  });
                  statusChangeHandler({
                    id: item?._id,
                    status: item?.status === 1 ? 2 : 1,
                  });
                }}
              />
              <Badge
                type={item?.status === 1 ? "success" : "danger"}
                label={item?.status === 1 ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },

            {
              _id: 2,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit Application Form =================>
  function applicationFormManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({ ...pre, data: [res?.data, ...pre?.data] }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: pre?.data?.map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message);
    }
  }

  //   Application Form delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await categoryDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        updateData((pre) => {
          pre.data = (pre?.data || [])?.filter(
            (item) => !payload?.includes(item?._id)
          );
          return { ...pre };
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          refetch();
        }
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });
  async function categoryrderingHandler(newData, oldData) {
    console.log({ newData, oldData });

    const ordering_data = newData?.map((item, i) => ({
      id: item?._id,
      ordering: i + 1,
    }));
    const res = await categoryOrdering({ ordering_data });
    if (res?.status !== 200) {
      updateData((pre) => {
        pre.data = oldData;
        return { ...pre };
      });
    }
  }

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          {/* {categoryType.charAt(0).toUpperCase() + categoryType.slice(1)}{" "} */}
          Categories
        </div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 0, setFilters);
              }}
            />
          </div>
          <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
              }
              buttonLabel={"Create New"}
              buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div>
          {/* <CsvPdfDownload data={data?.data} /> */}
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(newData, oldData) => {
          categoryrderingHandler(newData, oldData);
        }}
        originalData={[...(data?.data || [])]}
        setOriginalData={(data) =>
          updateData((pre) => {
            pre.data = data;
            return { ...pre };
          })
        }
        isFetching={isFetching}
        isOrderingDisabled={false}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />
      <ManageCategoryModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={applicationFormManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Category;
